import { useCallback, useState } from "react";
import axiosClient from "@/utils/axios-client";
import { toCamelKeyObject } from "@/utils/case";
import useIdentity from "@/features/ui/hooks/use-identity";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

// (부티크) 라인시트별로 이 부티크에 들어온 오더의 수량을 합친 엑셀.
// ORIGIN = 발행사 양식(원본 파일), UNO_FORM = 우노폼 / ORDERED = 주문 수량 합, CONFIRMED = 확정 수량 합
// 합칠 오더는 셋 중 하나 — 전부(기본) / 고른 오더들(orderSheetSetIds, 합계 다운로드 모달) / 오더 한 건(orderSheetSetId, 오더 줄)
export type OrderTotalExportFormat = "ORIGIN" | "UNO_FORM";
export type OrderTotalExportQuantity = "ORDERED" | "CONFIRMED";

interface OrderTotalExportResponse {
  status: "PENDING" | "COMPLETED";
  requestId: string | null;
  fileName: string | null;
  url: string | null;
}

// 발행사 양식은 원본 파일에 수량을 채우는 작업이 따로 돌아서 다 될 때까지 기다린다
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 2 * 60 * 1000;

const exportUrl = (
  boutiqueId: number,
  lineSheetSetId: number,
  lineSheetId: number
) =>
  `/boutiques/${boutiqueId}/line-sheet-sets/${lineSheetSetId}/line-sheets/${lineSheetId}/order-total-exports`;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function triggerDownload(url: string) {
  const element = document.createElement("a");
  element.style.display = "none";
  element.href = url;
  element.download = "";
  element.target = "_blank";
  document.body.appendChild(element);
  element.click();
  setTimeout(() => element.remove(), 5 * 1000);
}

// orderSheetSetId 가 있으면 그 오더 한 건의 수량만(라인시트세트 상세의 오더 줄 다운로드),
// orderSheetSetIds 가 있으면 고른 오더들만(합계 다운로드 모달), 둘 다 없으면 전부
export const orderTotalExportKey = (
  lineSheetId: number,
  format: OrderTotalExportFormat,
  quantity: OrderTotalExportQuantity,
  orderSheetSetId?: number,
  orderSheetSetIds?: number[]
) => {
  const scope = orderSheetSetId
    ? `order:${orderSheetSetId}`
    : `total:${
        orderSheetSetIds?.length
          ? [...orderSheetSetIds].sort((a, b) => a - b).join("+")
          : "all"
      }`;
  return `${lineSheetId}_${format}_${quantity}_${scope}`;
};

const totalKeyPrefix = (
  lineSheetId: number,
  format: OrderTotalExportFormat,
  quantity: OrderTotalExportQuantity
) => `${lineSheetId}_${format}_${quantity}_total:`;

export default function useBoutiqueOrderTotalExport(lineSheetSetId?: number) {
  const identity = useIdentity();
  const { error: showError, info: showInfo } = useAppToasts({ id: "GLOBAL" });
  const { tTitle } = useI18nHelper();
  // 만드는 중인 파일(라인시트·양식·수량·오더 범위) — 같은 버튼을 다시 누르지 못하게 로딩으로 보여준다
  const [loadingKeys, setLoadingKeys] = useState<string[]>([]);

  const exportOrderTotal = useCallback(
    async (
      lineSheetId: number,
      format: OrderTotalExportFormat,
      quantity: OrderTotalExportQuantity,
      orderSheetSetId?: number,
      orderSheetSetIds?: number[]
    ) => {
      const boutiqueId = identity?.company?.id;
      if (!boutiqueId || !lineSheetSetId) {
        return;
      }
      // 오더 한 건이면 고른 목록은 무시한다(백엔드도 한 건을 우선한다)
      const selectedIds =
        !orderSheetSetId && orderSheetSetIds?.length
          ? orderSheetSetIds
          : undefined;
      const key = orderTotalExportKey(
        lineSheetId,
        format,
        quantity,
        orderSheetSetId,
        selectedIds
      );
      setLoadingKeys((prev) => [...prev, key]);
      try {
        const url = exportUrl(boutiqueId, lineSheetSetId, lineSheetId);
        let result = toCamelKeyObject(
          (
            await axiosClient.post(url, {
              format,
              quantity,
              order_sheet_set_id: orderSheetSetId ?? null,
              order_sheet_set_ids: selectedIds ?? null,
            })
          ).data
        ) as OrderTotalExportResponse;

        if (result.status === "PENDING" && result.requestId) {
          showInfo(tTitle("common:boutique_order_total.preparing"));
          const startedAt = Date.now();
          while (result.status === "PENDING") {
            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
              showError(tTitle("common:boutique_order_total.timeout"));
              return;
            }
            await sleep(POLL_INTERVAL_MS);
            result = toCamelKeyObject(
              (
                await axiosClient.get(`${url}/${result.requestId}`, {
                  params: {
                    quantity,
                    ...(orderSheetSetId
                      ? { order_sheet_set_id: orderSheetSetId }
                      : {}),
                    // 배열은 order_sheet_set_ids=1&order_sheet_set_ids=2 로 나간다(axios-client 의 qs repeat)
                    ...(selectedIds ? { order_sheet_set_ids: selectedIds } : {}),
                  },
                })
              ).data
            ) as OrderTotalExportResponse;
          }
        }
        if (result.url) {
          triggerDownload(result.url);
        }
      } catch (e) {
        showError(e);
      } finally {
        setLoadingKeys((prev) => prev.filter((k) => k !== key));
      }
    },
    [identity, lineSheetSetId, showError, showInfo, tTitle]
  );

  // 합계 버튼(오더 한 건이 아닌 경우)은 어떤 오더를 골랐든 그 라인시트·양식·수량의 합계를 만드는 중이면 로딩
  const isExporting = useCallback(
    (
      lineSheetId: number,
      format: OrderTotalExportFormat,
      quantity: OrderTotalExportQuantity,
      orderSheetSetId?: number
    ) =>
      orderSheetSetId
        ? loadingKeys.includes(
            orderTotalExportKey(lineSheetId, format, quantity, orderSheetSetId)
          )
        : loadingKeys.some((k) =>
            k.startsWith(totalKeyPrefix(lineSheetId, format, quantity))
          ),
    [loadingKeys]
  );

  return { exportOrderTotal, isExporting };
}
